import React, { useEffect } from 'react'
import { observer, useLocalObservable } from 'mobx-react-lite'
import axiosService from '../utils/AxiosService'
import { toastError, toastSuccess } from '../components/FormComponents/Toasts'
import { useParams, useNavigate } from 'react-router-dom'
export const BrowseContext = React.createContext({})

export default observer(function BrowseProvider(props) {
  const { client_id, folder_id } = useParams()
  const navigate = useNavigate()
  
  const browseState = useLocalObservable(() => (
      {
        resultsFilter: 'all',
        allCampaigns: [],
        recentlyOpenedCampaigns: [],
        clients: [],
        folders: [],
        selectedClient: {}, // new Client(), //campaigns by client, edit and delete modal switchs
        selectedFolder: {}, // new Folder(), // campaigns by folder, edit modal switch
        selectedCampaignId: null,
        selectedCampaignFolderId: null,
        clientCampaigns: [],
        openCreateClient: false,
        toggleCreateClient() { this.openCreateClient = !this.openCreateClient },
        openCreateFolder: false,
        toggleCreateFolder() { this.openCreateFolder = !this.openCreateFolder },
        openDeleteClient: false,
        toggleDeleteClient() { this.openDeleteClient = !this.openDeleteClient },
        openEditClient: false,
        toggleEditClient() {
          this.openEditClient = !this.openEditClient
          if (!this.openEditClient) this.editClientInitialPage = 'firstPage'
        },
        editClientInitialPage: 'firstPage',
        openEditClientContacts() {
          this.editClientInitialPage = 'secondPage'
          this.openEditClient = true
        },
        openShareClient: false,
        toggleShareClient() { this.openShareClient = !this.openShareClient },
        openEditFolder: false,
        toggleEditFolder() { this.openEditFolder = !this.openEditFolder },
        openUpdateCampaignFolder: false,
        toggleUpdateCampaignFolder() { this.openUpdateCampaignFolder = !this.openUpdateCampaignFolder },
        clientListSort: { direction: 'asc', attribute: 'name' },
        folderListSort: { direction: 'asc', attribute: 'name' },
        campaignListSort: { direction: 'asc', attribute: 'name'},

        updateCampaignFolder(folder_id) {
          return axiosService.patch(`/api/project_manager/campaigns/${this.selectedCampaignId}/update_folder`, { folder_id })
          .then((updatedCampaign) => {
            // Update folders state
            const oldFolder = this.folders.find(f => +f.id === +this.selectedCampaignFolderId)
            if (oldFolder) {
              oldFolder.campaign_count -= 1
            }
            const newFolder = this.folders.find(f => +f.id === +updatedCampaign.folder_id)
            if (newFolder) {
              newFolder.campaign_count += 1
            }

            // Update campaign in allCampaigns array
            const campaignIndex = this.allCampaigns.findIndex(c => c.id === updatedCampaign.id)
            if (campaignIndex !== -1) {
              this.allCampaigns[campaignIndex] = updatedCampaign
            }

            // Update campaign in clientCampaigns array
            const clientCampaignIndex = this.clientCampaigns.findIndex(c => c.id === updatedCampaign.id)
            if (clientCampaignIndex !== -1) {
              this.clientCampaigns[clientCampaignIndex] = updatedCampaign
            }

            this.toggleUpdateCampaignFolder()
            navigate(`/project_manager/campaigns/clients/${updatedCampaign.client_id}/folders/${updatedCampaign.folder_id}`)
            toastSuccess(`${updatedCampaign.name} has been moved!`)
            return updatedCampaign
          })
          .catch((error) => toastError(error))
        },

        adjustClientFolderCount(clientId, delta) {
          const client = this.clients.find(c => +c.id === +clientId)
          if (client) client.folder_count = Math.max(0, (client.folder_count || 0) + delta)
        },

        archiveClient(client) {
          return axiosService.patch(`/api/project_manager/clients/${client.id}/archive`)
            .then(() => {
              const belongsToClient = item => +item.client_id === +client.id
              this.clients = this.clients.filter(c => +c.id !== +client.id)
              this.folders = this.folders.filter(f => !belongsToClient(f))
              this.allCampaigns = this.allCampaigns.filter(c => !belongsToClient(c))
              this.clientCampaigns = this.clientCampaigns.filter(c => !belongsToClient(c))
              this.recentlyOpenedCampaigns = this.recentlyOpenedCampaigns.filter(c => !belongsToClient(c))

              if (window.location.pathname.startsWith(`/project_manager/campaigns/clients/${client.id}`)) {
                navigate('/project_manager/campaigns')
              }
              toastSuccess(`${client.name} has been archived`)
            })
            .catch(({ data: { error } }) => toastError(error))
        },

        archiveFolder(folder) {
          return axiosService.patch(`/api/project_manager/folders/${folder.id}/archive`)
            .then(() => {
              const inFolder = campaign => +campaign.folder_id === +folder.id
              this.folders = this.folders.filter(f => +f.id !== +folder.id)
              this.allCampaigns = this.allCampaigns.filter(c => !inFolder(c))
              this.clientCampaigns = this.clientCampaigns.filter(c => !inFolder(c))
              this.recentlyOpenedCampaigns = this.recentlyOpenedCampaigns.filter(c => !inFolder(c))
              this.adjustClientFolderCount(folder.client_id, -1)
              const client = this.clients.find(c => +c.id === +folder.client_id)
              if (client) client.campaign_count = Math.max(0, client.campaign_count - (folder.campaign_count || 0))

              if (window.location.pathname.includes(`/folders/${folder.id}`)) {
                navigate(`/project_manager/campaigns/clients/${folder.client_id}`)
              }
              toastSuccess(`${folder.name} has been archived`)
            })
            .catch(({ data: { error } }) => toastError(error))
        },

        archiveCampaign(campaign) {
          return axiosService.patch(`/api/project_manager/campaigns/${campaign.id}/archive`)
            .then(() => {
              this.allCampaigns = this.allCampaigns.filter(c => c.id !== campaign.id)
              this.clientCampaigns = this.clientCampaigns.filter(c => c.id !== campaign.id)
              this.recentlyOpenedCampaigns = this.recentlyOpenedCampaigns.filter(c => c.id !== campaign.id)

              const client = this.clients.find(c => +c.id === +campaign.client_id)
              if (client) client.campaign_count -= 1
              const folder = this.folders.find(f => +f.id === +campaign.folder_id)
              if (folder) folder.campaign_count -= 1

              toastSuccess(`${campaign.name} has been archived`)
            })
            .catch(({ data: { error } }) => toastError(error))
        }
      }
  ))

  const getCampaigns = () => {
    axiosService.get('/api/project_manager/campaigns', { params: {filter: browseState.resultsFilter} })
      .then(campaigns => browseState.allCampaigns = campaigns)
      .catch(({ data: { error } }) => toastError(error))
  }

  const getRecentlyOpenedCampaigns = () => {
    axiosService.get('/api/project_manager/campaigns/recently_opened_campaigns')
      .then(campaigns => browseState.recentlyOpenedCampaigns = campaigns)
      .catch(error => toastError(error))
  }

  const getClients = () => {
    axiosService.get('/api/project_manager/clients',
      {
        params:
        {
          filter: browseState.resultsFilter,
          sort_by: browseState.clientListSort.attribute,
          decending: browseState.clientListSort.direction
        }
      })
      .then(clients => browseState.clients = clients)
      .catch(({ data: { error } }) => toastError(error))
  }

  const getFolders = () => {
    axiosService.get('/api/project_manager/folders',
      {
        params:
        {
          filter: browseState.resultsFilter,
          sort_by: browseState.folderListSort.attribute,
          decending: browseState.folderListSort.direction
        }
      })
      .then(folders => browseState.folders = folders)
      .catch(({ data: { error } }) => toastError(error))
  }

  const getClientFolders = () => {
    axiosService.get(`/api/project_manager/clients/${client_id}/folders`,
      {
        params:
        {
          filter: browseState.resultsFilter,
          sort_by: browseState.folderListSort.attribute,
          decending: browseState.folderListSort.direction
        }
      })
      .then(folders => browseState.folders = folders)
      .catch(({ data: { error } }) => toastError(error))
  }

  const getClientCampaigns = () => {
    if (client_id) {
      axiosService.get(`/api/project_manager/clients/${client_id}/campaigns`,
        {
          params: {
            filter: browseState.resultsFilter,
            sort_by: browseState.campaignListSort.attribute,
            decending: browseState.campaignListSort.direction
          }
        })
        .then(campaigns => {
          browseState.clientCampaigns = campaigns
    })
        .catch(({ data: { error } }) => toastError(error))
    }
  }

  const getSelectedClient = () => {
    if (client_id) {
      axiosService.get(`/api/project_manager/clients/${client_id}`)
        .then(client => browseState.selectedClient = client) // new Client(client)
        .catch(({ data: { error } }) => toastError(error))
    } else {
      browseState.selectedClient = {} // new Client()
    }
  }

  const getSelectedFolder = () => {
    if (folder_id) {
      axiosService.get(`/api/project_manager/folders/${folder_id}`)
        .then(folder => browseState.selectedFolder = folder) // new Folder(folder)
        .catch(({ data: { error } }) => toastError(error))
    } else {
      browseState.selectedFolder = {} // new Folder()
    }
  }

  const reloadBrowse = () => {
    getRecentlyOpenedCampaigns()
    getCampaigns()
    getClients()
    client_id ? getClientFolders() : getFolders()
    getClientCampaigns()
  }

  useEffect(() => getRecentlyOpenedCampaigns(), [])
  useEffect(() => getCampaigns(), [browseState.resultsFilter])
  useEffect(() => getClients(), [browseState.resultsFilter, browseState.clientListSort.attribute, browseState.clientListSort.direction, browseState.clients.length])
  useEffect(() => getSelectedClient(), [client_id])
  useEffect(() => getSelectedFolder(), [folder_id])

  useEffect(() => getClientCampaigns(), [client_id, browseState.resultsFilter, browseState.campaignListSort.attribute, browseState.campaignListSort.direction])

  useEffect(() => {
    client_id ? getClientFolders() : getFolders()
  }, [
    browseState.resultsFilter,
    browseState.folderListSort.attribute,
    browseState.folderListSort.direction,
    client_id
  ])

  return (
    <BrowseContext.Provider
      value={{
        browseState,
        reloadBrowse,
        allCampaigns: browseState.allCampaigns,
        recentlyOpenedCampaigns: browseState.recentlyOpenedCampaigns,
        clientCampaigns: browseState.clientCampaigns,
        clients: browseState.clients,
        folders: browseState.folders,
        selectedClient: browseState.selectedClient,
        selectedFolder: browseState.selectedFolder,
        resultsFilter: browseState.resultsFilter,
        openCreateClient: browseState.openCreateClient,
        openCreateFolder: browseState.openCreateFolder,
        openEditClient: browseState.openEditClient,
        openShareClient: browseState.openShareClient,
        openEditFolder: browseState.openEditFolder,
        openUpdateCampaignFolder: browseState.openUpdateCampaignFolder,
        openDeleteClient: browseState.openDeleteClient,
        clientListSort: browseState.clientListSort,
        folderListSort: browseState.folderListSort,
        campaignListSort: browseState.campaignListSort,
      }}
    >
      {props.children}
    </BrowseContext.Provider>
  )
})
